export type BrandMessageType =
  | 'TEXT'
  | 'IMAGE'
  | 'WIDE'
  | 'CAROUSEL_FEED'
  | 'WIDE_ITEM_LIST'
  | 'COMMERCE'
  | 'CAROUSEL_COMMERCE'
  | 'PREMIUM_VIDEO';

export const BRAND_MESSAGE_TYPE_OPTIONS = [
  { id: 'TEXT', text: '텍스트' },
  { id: 'IMAGE', text: '이미지' },
  { id: 'WIDE', text: '와이드이미지' },
  { id: 'CAROUSEL_FEED', text: '캐러셀' },
  { id: 'WIDE_ITEM_LIST', text: '리스트' },
  { id: 'COMMERCE', text: '커머스' },
  { id: 'CAROUSEL_COMMERCE', text: '캐러셀커머스' },
] as const;

export const BRAND_MESSAGE_TYPE_LABELS: Record<BrandMessageType, string> = {
  TEXT: '텍스트',
  IMAGE: '이미지',
  WIDE: '와이드이미지',
  CAROUSEL_FEED: '캐러셀',
  WIDE_ITEM_LIST: '리스트',
  COMMERCE: '커머스',
  CAROUSEL_COMMERCE: '캐러셀커머스',
  PREMIUM_VIDEO: '프리미엄 동영상',
};

export const getBrandMessageTypeLabel = (type: string) => BRAND_MESSAGE_TYPE_LABELS[type as BrandMessageType] ?? type;

/**
 * 유형별 입력 제한. 카카오 브랜드메시지 등록 규격이라 더 늘릴 수 없고,
 * 기본으로 정해진 값(캐러셀 카드당 버튼 1개 등)은 더 줄일 수도 없다.
 */
export type BrandMessageLimit = {
  /** 버튼 최대 개수 */
  buttonMax: number;
  /** 쿠폰을 넣은 경우의 버튼 최대 개수 (없으면 buttonMax 와 동일) */
  buttonMaxWithCoupon?: number;
  /** 버튼명 최대 글자수 */
  buttonNameMax: number;
  /** 본문 최대 글자수 (본문이 없는 유형은 undefined) */
  contentMax?: number;
  /** 본문 줄바꿈 최대 횟수 */
  contentNewlineMax?: number;
};

export const BRAND_MESSAGE_LIMITS: Record<BrandMessageType, BrandMessageLimit> = {
  TEXT: { buttonMax: 5, buttonMaxWithCoupon: 4, buttonNameMax: 14, contentMax: 1300, contentNewlineMax: 99 },
  IMAGE: { buttonMax: 5, buttonMaxWithCoupon: 4, buttonNameMax: 14, contentMax: 1300, contentNewlineMax: 99 },
  WIDE: { buttonMax: 2, buttonNameMax: 8, contentMax: 76, contentNewlineMax: 5 },
  CAROUSEL_FEED: { buttonMax: 2, buttonNameMax: 8 },
  WIDE_ITEM_LIST: { buttonMax: 2, buttonNameMax: 8 },
  COMMERCE: { buttonMax: 2, buttonNameMax: 8 },
  CAROUSEL_COMMERCE: { buttonMax: 2, buttonNameMax: 8 },
  PREMIUM_VIDEO: { buttonMax: 1, buttonNameMax: 8, contentMax: 76, contentNewlineMax: 5 },
};

/** 캐러셀 카드 1장의 제한 (캐러셀 / 캐러셀커머스 공통) */
export const CAROUSEL_ITEM_LIMITS = {
  headerMax: 20,
  contentMax: 180,
  contentNewlineMax: 10,
  /** 카드마다 버튼 1개는 기본 — 더 줄일 수 없다 */
  buttonMin: 1,
  buttonMax: 2,
  buttonNameMax: 8,
} as const;

/** 리스트(와이드 아이템 리스트) 제한 */
export const WIDE_ITEM_LIST_LIMITS = {
  headerMax: 20,
  mainTitleMax: 25,
  subTitleMax: 30,
  /** 각 아이템 타이틀은 줄바꿈 1회까지 */
  titleNewlineMax: 1,
  subItemMin: 2,
  subItemMax: 3,
} as const;

/** 커머스 부가정보는 줄바꿈 1회까지 (커머스 / 캐러셀커머스 공통) */
export const COMMERCE_ADDITIONAL_CONTENT_NEWLINE_MAX = 1;

/**
 * 가격 고정변수명. 카카오가 정한 이름이라 바꿀 수 없고, 템플릿에는 네 개가 모두 등록된다
 * (비즈뿌리오가 네 개를 다 저장해 돌려주므로 조합을 템플릿에 남길 수 없다).
 * 어떤 조합으로 노출할지는 **발송 쿼리의 컬럼**이 정한다.
 */
export const COMMERCE_PRICE_VARIABLE_FIELDS = [
  { label: '정상 가격(원)', variable: '#{정상가격}' },
  { label: '할인 후 가격(원)', variable: '#{할인가격}' },
  { label: '할인율(%)', variable: '#{할인율}' },
  { label: '정액할인가격(원)', variable: '#{정액할인가격}' },
] as const;

export const BRAND_MESSAGE_VARIABLE_RULES = [
  '변수는 #{변수명} 규칙으로 입력해주세요.',
  '변수명은 최대 20자 이내, 한글·영어 소문자·숫자·_ 만 쓸 수 있습니다. 숫자로 시작하거나 공백·대문자는 쓸 수 없습니다.',
  '동일한 변수값을 사용할 경우 변수명 중복 입력 가능합니다.',
  '변수는 최대 20개까지 입력 가능합니다.',
] as const;

export const BRAND_MESSAGE_VARIABLE_MAX_COUNT = 20;

/** 줄바꿈 횟수 = 개행 문자 수 */
export const countNewlines = (value?: string | null) => (value ? value.split('\n').length - 1 : 0);

/** 줄바꿈을 max 회까지만 남기고 잘라낸다(초과 입력 차단용). */
export const clampNewlines = (value: string, max: number) => {
  const parts = value.split('\n');
  if (parts.length - 1 <= max) return value;
  return parts.slice(0, max + 1).join('\n') + parts.slice(max + 1).join('');
};

export const getBrandMessageLimit = (type: BrandMessageType) => BRAND_MESSAGE_LIMITS[type];

/** 쿠폰 유무까지 반영한 버튼 최대 개수 */
export const getButtonMax = (type: BrandMessageType, hasCoupon: boolean) => {
  const limit = BRAND_MESSAGE_LIMITS[type];
  return hasCoupon ? limit.buttonMaxWithCoupon ?? limit.buttonMax : limit.buttonMax;
};
